"use client";
import { cn } from "@/utils/common.utils";
import {
  FormControl,
  FormHelperText,
  InputLabelProps,
  SwitchProps,
} from "@mui/material";
import React, { ReactNode, memo } from "react";
import {
  Control,
  Controller,
  ControllerProps,
  FieldPath,
  FieldValues,
  RegisterOptions,
} from "react-hook-form";
import AppToggle from "../AppToggle";
import AppFromLabelField from "./AppFromLabelField";

const AppFormControlToggle = <T extends FieldValues>({
  label,
  control,
  name,
  rules,
  controlProps,
  toggleProps,
  labelProps,
  onChangeValueForm,
  classNameContainer,
  helper,
}: AppFormControlToggleProps<T>) => {
  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({
        field: { onChange, value, ...otherFieldProps },
        fieldState: { error: errorField },
      }) => (
        <>
          <AppFromLabelField
            label={label}
            labelProps={{
              ...labelProps,
              className: cn("mb-0", labelProps?.className),
              sx: {
                marginBottom: "0",
                ...labelProps?.sx,
              },
            }}
            className={cn("flex items-center gap-2", classNameContainer)}
            required={labelProps?.required}
            helper={helper}
          >
            <FormControl
              error={!!errorField}
              margin="normal"
              sx={{
                display: "flex",
                margin: 0,
              }}
            >
              <AppToggle
                onChange={(event, checked) => {
                  onChange(event);
                  if (onChangeValueForm instanceof Function)
                    onChangeValueForm(event, checked);
                }}
                {...otherFieldProps}
                checked={value}
                {...toggleProps}
              />
            </FormControl>
          </AppFromLabelField>
          {errorField && (
            <FormHelperText
              className="text-error"
              sx={{
                margin: "3px 14px 0",
              }}
            >
              {errorField?.message}
            </FormHelperText>
          )}
        </>
      )}
      {...controlProps}
    />
  );
};

type AppFormControlToggleProps<T extends FieldValues> = {
  label?: string | ReactNode;
  control: Control<any>;
  name: FieldPath<T>;

  rules?: RegisterOptions;
  controlProps?: Omit<ControllerProps, "render" | "name" | "control">;
  labelProps?: InputLabelProps;
  toggleProps?: SwitchProps;
  onChangeValueForm?: (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => void;
  classNameContainer?: string;
  helper?: string | ReactNode;
};

export default memo(AppFormControlToggle);
